import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, fieldIssues } from '../api';
import { ConflictNotice } from '../ui/ConflictNotice';
import type { SendPreview, TransitionResult, Version } from './types';
import { DialogLayer } from '../ui/Dialog';

/**
 * "Wyślij do klienta" (F30): the client's e-mail (from the contractor, editable), "DW", subject and text from the
 * template of "Ustawienia kosztorysu". "Wyślij" freezes the draft and queues the e-mail with its PDF; the dialog is
 * the confirmation (nothing else asks). "Podgląd PDF" shows the attachment (in the e-mail without "PROJEKT").
 */
export function SendDialog({ version, onDone, onClose, onReload, onPreview }: { version: Version; onDone: (v: TransitionResult) => void; onClose: () => void; onReload: () => void; onPreview: () => void }) {
  const preview = useQuery({ queryKey: ['quotes', 'send-preview', version.id], queryFn: () => api<SendPreview>(`/api/quotes/versions/${version.id}/send-preview`), staleTime: Infinity, gcTime: 0 });
  // typed values win; until then the preview's
  const [typed, setTyped] = useState<Partial<SendPreview> & { cc?: string }>({});
  const [local, setLocal] = useState('');
  const form = { to: typed.to ?? preview.data?.to ?? '', cc: typed.cc ?? '', subject: typed.subject ?? preview.data?.subject ?? '', body: typed.body ?? preview.data?.body ?? '' };
  const send = useMutation({
    mutationFn: () => api<TransitionResult>(`/api/quotes/versions/${version.id}/send`, { method: 'POST', body: { ...form, version: version.version } }),
    onSuccess: onDone,
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    if (form.to.trim() === '') return setLocal('Wpisz adres e-mail klienta');
    if (form.subject.trim() === '') return setLocal('Wpisz temat wiadomości');
    setLocal('');
    send.mutate();
  }
  const error = send.error instanceof ApiError ? send.error : preview.error instanceof ApiError ? preview.error : null;
  // somebody changed or sent this version meanwhile: the screen is out of date, read it again
  const stale = !!error && (error.status === 409 || error.code === 'version_frozen');
  const set = (patch: Partial<SendPreview> & { cc?: string }) => setTyped((t) => ({ ...t, ...patch }));
  return (
    <div className="dialog-backdrop">
      <form className="dialog panel q-send" role="dialog" aria-modal="true" aria-labelledby="send-title" onSubmit={submit} data-feature-id="quotes.send">
        <DialogLayer onClose={onClose} busy={send.isPending} />
        <h2 id="send-title">Wyślij v{version.number} do klienta</h2>
        <p className="muted">E-mail z PDF kosztorysu w załączniku. Wersja zostanie zamrożona — poprawki zrobisz potem w nowej wersji.</p>
        {stale && <ConflictNotice onReload={onReload} />}
        {preview.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
        <label className="field">
          <span>Do</span>
          <input type="email" value={form.to} onChange={(e) => set({ to: e.target.value })} autoComplete="off" autoFocus placeholder="adres e-mail klienta" />
        </label>
        {!preview.isLoading && !preview.data?.to && !typed.to && <p className="hint">Klient wydarzenia nie ma adresu e-mail w karcie kontrahenta — wpisz go tutaj albo uzupełnij w „Kontrahenci”.</p>}
        <label className="field">
          <span>DW (nieobowiązkowo)</span>
          <input type="email" value={form.cc} onChange={(e) => set({ cc: e.target.value })} autoComplete="off" placeholder="np. Twój adres, kopia dla siebie" />
        </label>
        <label className="field">
          <span>Temat</span>
          <input value={form.subject} maxLength={300} onChange={(e) => set({ subject: e.target.value })} autoComplete="off" />
        </label>
        <label className="field">
          <span>Treść</span>
          <textarea rows={6} value={form.body} maxLength={10000} onChange={(e) => set({ body: e.target.value })} />
        </label>
        <p className="q-send-attachment">
          Załącznik: PDF kosztorysu v{version.number}{' '}
          <button className="btn small ghost" type="button" onClick={onPreview}>
            Podgląd PDF
          </button>
        </p>
        <p className="hint">
          Szablon tematu i treści: <Link to="/finanse/cenniki/ustawienia-kosztorysu">Zarządzaj ustawieniami kosztorysu…</Link>
        </p>
        {(local || (error && !stale)) && (
          <p className="alert" role="alert">
            {local || (error && (fieldIssues(error)[0]?.message ?? error.message))}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="submit" disabled={send.isPending || preview.isLoading}>
            {send.isPending ? 'Wysyłanie…' : 'Wyślij'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}
